import { useState } from 'react';
import { useI18n } from '../i18n';
import { setMapShared } from '../lib/api';
import { describeError } from '../lib/errors';
import { env } from '../config/env';

/**
 * Make one map publicly viewable by link.
 *
 * The share id is not the map id: the API mints a separate unguessable id so
 * that turning sharing off and on again invalidates the old link, and so that
 * a map id appearing in a log does not become a public URL.
 */
export function shareUrl(shareId: string): string {
  const base = env.publicBaseUrl || (typeof window === 'undefined' ? '' : window.location.origin);
  return `${base}/m/${encodeURIComponent(shareId)}`;
}

export function ShareToggle({
  mapId,
  shared,
  shareId,
  onChange,
  compact = false,
}: {
  mapId: string;
  shared: boolean;
  shareId: string | null;
  onChange: (shared: boolean, shareId: string | null) => void;
  compact?: boolean;
}) {
  const i18n = useI18n();
  const { t } = i18n;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const toggle = async (next: boolean) => {
    setBusy(true);
    setError(null);
    try {
      const result = await setMapShared(mapId, next);
      onChange(result.shared, result.share_id);
    } catch (cause) {
      setError(t('saved.share.failed', { message: describeError(cause, i18n).message }));
    } finally {
      setBusy(false);
    }
  };

  const url = shared && shareId ? shareUrl(shareId) : null;

  const copy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard permission denied; the input is selectable, which is enough.
    }
  };

  return (
    <div className="share">
      <label className="switch">
        <input
          type="checkbox"
          checked={shared}
          disabled={busy}
          onChange={(event) => void toggle(event.target.checked)}
        />
        <span className="switch__track">
          <span className="switch__thumb" />
        </span>
        <span>{shared ? t('saved.share.on') : t('saved.share.off')}</span>
      </label>

      {!compact ? <p className="field__hint">{t('saved.share.hint')}</p> : null}

      {url ? (
        <div className="share__link">
          <input
            className="input share__url"
            type="text"
            readOnly
            value={url}
            aria-label={t('saved.share.link')}
            onFocus={(event) => event.currentTarget.select()}
          />
          <button type="button" className="button" onClick={() => void copy()}>
            {copied ? t('common.copied') : t('common.copy')}
          </button>
        </div>
      ) : null}

      {error ? <p className="field__error">{error}</p> : null}
    </div>
  );
}
